// Enter results.
//
// Selecting a result for one player automatically sets the mirror result
// for their opponent, so you cannot record two winners in the same match.
//   win     -> opponent loss
//   loss    -> opponent win
//   tie     -> opponent tie
//   forfeit -> opponent stays as-is (they receive 3 points automatically)

import React, { useMemo, useState } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import MatchCard from './MatchCard';
import ReadOnlyNotice from './ReadOnlyNotice';
import { pairsFromPairings } from '../utils/helpers';
import { BRACKET_ROUND_LABELS } from '../data/seedSchedule';

export default function ResultsList({
  matches, golfers, teeSets, courses, seasonDates, isAdmin, onChangeResult, onChangeTee,
}) {
  const [filter, setFilter] = useState('open');

  const matchStatus = (m) => {
    const pairs = pairsFromPairings(m.pairings).filter(([a, b]) => a && b);
    if (pairs.length === 0) return 'empty';
    const done = pairs.every(([a, b]) => {
      const ra = m.results?.[a] || 'pending';
      const rb = m.results?.[b] || 'pending';
      if (ra === 'forfeit' || rb === 'forfeit') return true;
      return ra !== 'pending' && rb !== 'pending';
    });
    return done ? 'complete' : 'open';
  };

  const list = useMemo(() => {
    const all = (matches || []).slice().sort((a, b) =>
      String(a.match_date).localeCompare(String(b.match_date)) ||
      String(a.tee_time).localeCompare(String(b.tee_time)));
    if (filter === 'open') return all.filter(m => matchStatus(m) === 'open');
    if (filter === 'complete') return all.filter(m => matchStatus(m) === 'complete');
    return all;
  }, [matches, filter]);

  const counts = useMemo(() => {
    const c = { open: 0, complete: 0, empty: 0 };
    (matches || []).forEach(m => { c[matchStatus(m)] = (c[matchStatus(m)] || 0) + 1; });
    return c;
  }, [matches]);

  const dateLabel = (iso) => (seasonDates || []).find(d => d.play_date === iso)?.label || '';

  return (
    <div className="space-y-4">
      {!isAdmin && <ReadOnlyNotice>Results are view-only. Unlock admin to enter scores.</ReadOnlyNotice>}

      <div className="flex flex-wrap items-center gap-1">
        {[['open', `Awaiting results (${counts.open})`],
          ['complete', `Complete (${counts.complete})`],
          ['all', `All (${matches?.length || 0})`]].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium ${
              filter === v ? 'bg-gray-900 text-white' : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
        Win 5 &middot; Tie 3 &middot; Loss 1 &middot; Forfeit 0 (the opponent of a forfeit receives 3).
        Setting one player's result fills in their opponent's automatically.
      </div>

      {list.length === 0 && (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Nothing here.
        </div>
      )}

      {list.map(m => {
        const status = matchStatus(m);
        return (
          <MatchCard
            key={m.id}
            match={m}
            golfers={golfers}
            teeSets={teeSets}
            courses={courses}
            isAdmin={isAdmin}
            showDate
            onChangeResult={onChangeResult}
            onChangeTee={onChangeTee}
            headerExtra={
              <span className="flex items-center gap-2">
                {m.phase === 'playoff' && (
                  <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-800">
                    {BRACKET_ROUND_LABELS[m.bracket_round] || 'Playoff'}
                  </span>
                )}
                <span className="text-xs text-gray-500">{dateLabel(m.match_date)}</span>
                {status === 'complete'
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-600" title="Complete" />
                  : <Circle className="h-4 w-4 text-gray-300" title="Awaiting results" />}
              </span>
            }
          />
        );
      })}
    </div>
  );
}
